/**
 * VocabularyBindingImpl - Wraps any QueryableRDFDataset to produce a SKOS Vocabulary.
 *
 * Uses SPARQL CONSTRUCT to extract SKOS triples, builds an N3.Store from quads,
 * and returns a Vocabulary instance. Works with ANY QueryableRDFDataset source.
 */
import N3 from 'n3';
import Vocabulary from './Vocabulary';
const SKOS_CONSTRUCT_QUERY = `
CONSTRUCT { ?s ?p ?o . }
WHERE { ?s ?p ?o . }
`;
/**
 * Create a VocabularyBinding that wraps a QueryableRDFDataset.
 *
 * The returned binding delegates to the given config for identity/metadata
 * and uses the source for SPARQL queries to build a SKOS Vocabulary.
 */
/**
 * Create a VocabularyBinding reference with minimal identity data.
 *
 * This is NOT a stub — it creates a real VocabularyBinding whose getVocabulary()
 * delegates to the injected fetchVocabulary function. On the client, this function
 * calls the BFF endpoint. On the server, it uses the storage chain directly.
 *
 * If no fetchVocabulary is provided, getVocabulary() will throw — the binding
 * must be hydrated before use.
 */
export function createVocabularyBindingRef(config) {
    let cachedVocabulary = null;
    return {
        id: config.id,
        name: config.name,
        boundedContextId: config.boundedContextId ?? '',
        dataset: undefined,
        graphMapping: undefined,
        namespace: config.namespace ?? '',
        vocabularyIri: config.vocabularyIri,
        async getVocabulary() {
            if (cachedVocabulary) {
                return cachedVocabulary;
            }
            if (!config.fetchVocabulary) {
                throw new Error(`VocabularyBinding '${config.name}' (${config.id}) has no fetchVocabulary — hydrate it first.`);
            }
            cachedVocabulary = await config.fetchVocabulary();
            return cachedVocabulary;
        },
    };
}
/**
 * Create a VocabularyBinding that wraps a QueryableRDFDataset.
 *
 * The returned binding delegates to the given config for identity/metadata
 * and uses the source for SPARQL queries to build a SKOS Vocabulary.
 */
export function createVocabularyBinding(config, source, vocabularyIri) {
    let cachedVocabulary = null;
    return {
        // Delegate identity from config
        id: config.id,
        name: config.name,
        boundedContextId: config.boundedContextId,
        dataset: config.dataset,
        graphMapping: config.graphMapping,
        namespace: config.namespace,
        description: config.description,
        vocabularyIri,
        async getVocabulary() {
            if (cachedVocabulary) {
                return cachedVocabulary;
            }
            const quads = await source.construct(SKOS_CONSTRUCT_QUERY);
            const store = new N3.Store();
            store.addQuads(quads);
            cachedVocabulary = new Vocabulary(store);
            return cachedVocabulary;
        },
    };
}
